import { ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { renameKeyFromTitleToText } from '@utils/objects.utils';
import { ButtonProps, TableHeadCell } from '@stadionhq-v3/base-ui';
import { ArrowRightLight } from '@components/icons';
import {
  FixtureSummaryBlockCardWrapper,
  StandingsRowHeader,
  StandingsTableRequestProps,
} from '@stadionhq-v3/football-ui';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import templateConfig from '@templateConfig';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Asset } from '@graphql/index';
import { Locale } from '@core/constants/locales.constants';
import { localeToContentfulLocale } from '@utils/locales.utils/locales.utils';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { TeamWithData } from '@contentful/model/pages/common/page-subqueries';
import buttonStyles from '@components/v3/atoms/button/button.module.scss';
import classnames from 'classnames';
import {
  ALIGNMENTS,
  STANDINGS_ROW_HEADERS,
  T_SHIRT_SIZES,
} from '@stadionhq-v3/core';
import { ERROR_MESSAGES } from '@core/constants/errorMessages';
import { FixtureSummaryBlockLeagueTable } from '@components/v3/organisms/fixture-summary-block-league-table';
import { ReactNode } from 'react';
import { linkTransform, assetTransform } from '../atoms';

export type FixtureSummaryBlockLeagueTableTranslations = {
  leagueTable: Translations['fixturesSummaryBlock']['label']['leagueTable'];
};

const fixtureSummaryBlockLeagueTableTransform = (
  team: TeamWithData,
  dictionary: FixtureSummaryBlockLeagueTableTranslations,
  locale: Locale,
  sportsApiLabels: StandingsTableRequestProps['dictionary'],
  currentSeason?: string,
  fallbackClubCrest?: CleanContentful<Asset>
): ReactNode => {
  if (!process.env.NEXT_PUBLIC_SPORTS_DATA_API_BASE_URL) {
    throw new Error(ERROR_MESSAGES.MISSING_ENV_VAR('Sports Data API'));
  }

  const headRowCells: TableHeadCell<StandingsRowHeader>[] = [
    {
      key: STANDINGS_ROW_HEADERS.POSITION,
      label: '',
      isBold: true,
    },
    {
      key: STANDINGS_ROW_HEADERS.POSITION_CHANGE,
      align: ALIGNMENTS.LEFT,
      size: T_SHIRT_SIZES.S,
    },
    { key: STANDINGS_ROW_HEADERS.CREST_URL, label: '' },
    {
      key: STANDINGS_ROW_HEADERS.CLUB_SHORT_NAME,
      label: '',
      isBold: true,
      align: ALIGNMENTS.LEFT,
      size: T_SHIRT_SIZES.XL,
    },
    {
      key: STANDINGS_ROW_HEADERS.POINTS,
      label: 'Pts',
      isBold: true,
      size: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.PLAYED,
      label: 'P',
      size: T_SHIRT_SIZES.L,
    },
    {
      key: STANDINGS_ROW_HEADERS.GOAL_DIFFERENCE,
      label: 'GD',
      size: T_SHIRT_SIZES.L,
    },
  ];

  const headRowCellsCondensed: TableHeadCell<StandingsRowHeader>[] = [
    {
      key: STANDINGS_ROW_HEADERS.POSITION,
      label: '',
      isBold: true,
    },
    { key: STANDINGS_ROW_HEADERS.CREST_URL, label: '' },
    {
      key: STANDINGS_ROW_HEADERS.CLUB_SHORT_NAME,
      label: '',
      isBold: true,
      align: ALIGNMENTS.LEFT,
      size: T_SHIRT_SIZES.XL,
    },
    {
      key: STANDINGS_ROW_HEADERS.POINTS,
      label: 'Pts',
      isBold: true,
      size: T_SHIRT_SIZES.M,
    },
  ];

  return (
    <FixtureSummaryBlockCardWrapper cardSubTitle={dictionary.leagueTable}>
      <FixtureSummaryBlockLeagueTable
        competitionId={team.primaryLeagueCompetition?.dataProviderId as string}
        competitionLogo={
          team.primaryLeagueCompetition?.logo ||
          team.primaryLeagueCompetition?.logoInverse ? (
            <ContentfulLogo
              {...assetTransform(
                (team.primaryLeagueCompetition.logo ||
                  team.primaryLeagueCompetition
                    .logoInverse) as CleanContentful<Asset>
              )}
              width={72}
              height={72}
            />
          ) : undefined
        }
        competitionName={team.primaryLeagueCompetition?.name ?? undefined}
        hasLeagueTable={Boolean(team.primaryLeagueCompetition?.hasLeagueTable)}
        teamId={team.dataProviderId as string}
        seasonId={currentSeason as string}
        SPORTS_DATA_API={{
          ...templateConfig.SPORTS_DATA_API,
          BASE_URL: process.env.NEXT_PUBLIC_SPORTS_DATA_API_BASE_URL as string,
          LOCALE: localeToContentfulLocale(locale),
        }}
        LEAGUE_TABLES_DATA={templateConfig.LEAGUE_TABLES_DATA}
        fallbackCrestUrl={fallbackClubCrest?.url as string}
        dictionary={sportsApiLabels}
        headRowCells={headRowCells}
        headRowCellsCondensed={headRowCellsCondensed}
        alternateRowsEnabled
        numberOfRowsToShow={{
          rowsAbove: 1,
          rowsBelow: 1,
        }}
        ctaLinks={[
          {
            ...(renameKeyFromTitleToText(
              linkTransform(
                {
                  url: generateFullPathname({
                    locale,
                    route: templateConfig.navigation.routes.TABLE_COMPETITION,
                    params: {
                      team: team.slug,
                      competition: team.primaryLeagueCompetition?.slug,
                    },
                  }),
                  title: sportsApiLabels.standings.fullTable,
                },
                locale,
                <ArrowRightLight />
              )
            ) as ButtonProps),
            variant: 'primary',
            classNames: [
              classnames([buttonStyles.button, buttonStyles.buttonTokens]),
            ],
          },
        ]}
      />
    </FixtureSummaryBlockCardWrapper>
  );
};

export default fixtureSummaryBlockLeagueTableTransform;
